/**
 * ui/services/account.ts
 *
 * The optional account this device is signed in to. The sign-in token is
 * kept in localStorage; signing out forgets it here and ends it on the
 * server. Nothing on the device is deleted by signing in or out.
 */
import { NO_ROLES, type Account, type AccountApiPort, type AccountRoles, type ProfileChanges } from '../../ports/account.js';
import { HttpAccountApi } from '../../adapters/http-account.js';
import { HttpError } from '../../adapters/http-errors.js';
import { browserStorage } from '../../utils/browser-storage.js';

export const ACCOUNT_STORAGE_KEY = 'nono_account';

interface StoredSignIn {
  token: string;
  account: Account;
}

type AccountStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export class AccountService {
  static readonly instance = new AccountService(new HttpAccountApi());

  private readonly _listeners = new Set<() => void>();
  /** Used when storage can't be read or written (private mode): the sign-in lasts for this page. */
  private _memory: StoredSignIn | null = null;
  /** The roles asked for this sign-in, while the app is open. */
  private _roles?: { token: string; roles: Promise<AccountRoles> };

  constructor(
    readonly api: AccountApiPort,
    /** Defaults to localStorage, looked up on each use. */
    private readonly _storageOverride?: AccountStorage,
  ) {
    // Another tab signed in or out.
    globalThis.addEventListener?.('storage', (event: StorageEvent) => {
      if (event.key === ACCOUNT_STORAGE_KEY) this._notify();
    });
  }

  /** The signed-in account, or null. */
  current(): Account | null {
    return this._read()?.account ?? null;
  }

  token(): string | null {
    return this._read()?.token ?? null;
  }

  async register(username: string, password: string): Promise<Account> {
    const signIn = await this.api.register(username.trim(), password);
    this._write(signIn);
    return signIn.account;
  }

  async login(username: string, password: string): Promise<Account> {
    const signIn = await this.api.login(username.trim(), password);
    this._write(signIn);
    return signIn.account;
  }

  /** Signs out here; the server forgets the token too when it can be reached. */
  async logout(): Promise<void> {
    const token = this.token();
    this._write(null);
    if (token) {
      try {
        await this.api.logout(token);
      } catch {
        // Offline: the token expires on its own.
      }
    }
  }

  async logoutEverywhere(): Promise<void> {
    const token = this._requireToken();
    await this.api.logoutEverywhere(token);
    this._write(null);
  }

  /**
   * What the signed-in account may do beyond playing (none when signed out),
   * asked once per sign-in while the app is open (pages ask on every visit).
   * A rejected token signs out.
   */
  roles(): Promise<AccountRoles> {
    const token = this.token();
    if (!token) return Promise.resolve(NO_ROLES);
    if (this._roles?.token !== token) {
      const roles = this._withToken(token, (t) => this.api.roles(t));
      this._roles = { token, roles };
      // A failure isn't remembered: the next page asks again.
      roles.catch(() => { if (this._roles?.roles === roles) this._roles = undefined; });
    }
    return this._roles.roles;
  }

  /** Checks the sign-in with the server and returns storage use. A rejected token signs out. */
  async refresh(): Promise<{ bytes: number; items: number } | null> {
    const token = this.token();
    if (!token) return null;
    const { account, usage } = await this._withToken(token, (t) => this.api.me(t));
    this._write({ token, account });
    return usage;
  }

  /** Calls the server with `token`; a rejected token signs out. */
  private async _withToken<T>(token: string, call: (token: string) => Promise<T>): Promise<T> {
    try {
      return await call(token);
    } catch (err) {
      if (err instanceof HttpError && err.status === 401) this.signedOutByServer(token);
      throw err;
    }
  }

  /** Changes the username and multiplayer color; listeners hear about it. */
  async updateProfile(changes: ProfileChanges): Promise<Account> {
    const token = this._requireToken();
    const { account } = await this.api.updateProfile(token, changes);
    if (this.token() === token) this._write({ token, account });
    return account;
  }

  async changePassword(current: string, next: string): Promise<void> {
    await this.api.changePassword(this._requireToken(), current, next);
  }

  /** Deletes the account and what's synced to it. This device's data stays. */
  async deleteAccount(password: string): Promise<void> {
    await this.api.deleteAccount(this._requireToken(), password);
    this._write(null);
  }

  /**
   * The server no longer accepts `token` (expired, signed out elsewhere).
   * Signs out only if that's still this device's token: an answer to an old
   * request mustn't sign out an account signed into since.
   */
  signedOutByServer(token: string): void {
    if (this._read()?.token === token) this._write(null);
  }

  onChanged(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  private _requireToken(): string {
    const token = this.token();
    if (!token) throw new Error('Sign in first');
    return token;
  }

  private get _storage(): AccountStorage | null {
    return this._storageOverride ?? browserStorage();
  }

  private _read(): StoredSignIn | null {
    let raw: string | null | undefined;
    try {
      const storage = this._storage;
      if (!storage) return this._memory;
      raw = storage.getItem(ACCOUNT_STORAGE_KEY);
    } catch {
      return this._memory;
    }
    try {
      const parsed = raw ? JSON.parse(raw) as Partial<StoredSignIn> : null;
      if (!parsed || typeof parsed.token !== 'string' || !parsed.account || typeof parsed.account.id !== 'string') return null;
      return parsed as StoredSignIn;
    } catch {
      return null;
    }
  }

  private _write(signIn: StoredSignIn | null): void {
    const before = this._read();
    this._memory = signIn;
    try {
      if (signIn) this._storage?.setItem(ACCOUNT_STORAGE_KEY, JSON.stringify(signIn));
      else this._storage?.removeItem(ACCOUNT_STORAGE_KEY);
    } catch {
      // Storage unavailable: the sign-in doesn't outlive the page.
    }
    const changed = before?.account.id !== signIn?.account.id || before?.account.username !== signIn?.account.username;
    if (changed) this._notify();
  }

  private _notify(): void {
    for (const listener of this._listeners) listener();
  }
}
